import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, ApiError, type SocialNetworkRow } from '../lib/api.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';

type Notice = { kind: 'ok' | 'error'; text: string } | null;

/** A starting point for a network nobody has described yet. */
const BLANK = {
  version: 1,
  allowedHosts: ['api.example.com'],
  auth: {
    type: 'oauth2',
    authorizeUrl: 'https://api.example.com/oauth/authorize',
    tokenUrl: 'https://api.example.com/oauth/token',
    scopes: [],
  },
  identity: {
    request: { method: 'GET', url: 'https://api.example.com/me' },
    idPath: 'id',
    namePath: 'name',
    accountId: '{{entry.id}}',
    kind: 'ACCOUNT',
  },
  publish: {
    kinds: ['TEXT'],
    request: { method: 'POST', url: 'https://api.example.com/posts', json: { text: '{{text}}' } },
    resultId: { from: 'body', path: 'id' },
  },
  permalink: 'https://example.com/posts/{{postId}}',
};

/**
 * Network definitions, for platform administrators.
 *
 * Every network other than Meta is a definition read by one engine, so adding
 * or adjusting a network happens here, as data, with no code change and no
 * deploy. The server checks a definition hard before keeping it: every address
 * must be one it declares, and every template must render.
 */
export function SocialNetworksAdmin({ onChanged }: { onChanged: () => void }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const networks = useQuery({ queryKey: ['social-networks'], queryFn: api.socialNetworks });
  const [editing, setEditing] = useState<{ key: string; name: string; json: string; isNew: boolean } | null>(
    null,
  );
  const [notice, setNotice] = useState<Notice>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['social-networks'] });
    onChanged();
  };
  const fail = (error: unknown) =>
    setNotice({
      kind: 'error',
      text: error instanceof ApiError ? error.message : t('common.requestFailed'),
    });

  const parse = (): unknown => {
    try {
      return JSON.parse(editing?.json ?? '');
    } catch {
      throw new ApiError(400, t('networks.badJson'));
    }
  };

  const check = useMutation({
    mutationFn: () => api.validateSocialNetwork(parse()),
    onSuccess: () => setNotice({ kind: 'ok', text: t('networks.valid') }),
    onError: fail,
  });
  const save = useMutation({
    mutationFn: () =>
      api.saveSocialNetwork(editing?.key.trim().toUpperCase() ?? '', {
        name: editing?.name.trim() ?? '',
        contract: parse(),
      }),
    onSuccess: () => {
      setNotice({ kind: 'ok', text: t('networks.saved') });
      setEditing(null);
      refresh();
    },
    onError: fail,
  });
  const status = useMutation({
    mutationFn: (args: { key: string; status: 'ACTIVE' | 'DISABLED' }) =>
      api.setSocialNetworkStatus(args.key, args.status),
    onSuccess: refresh,
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: api.deleteSocialNetwork,
    onSuccess: refresh,
    onError: fail,
  });

  const open = (row: SocialNetworkRow) =>
    setEditing({ key: row.key, name: row.name, json: JSON.stringify(row.contract, null, 2), isNew: false });

  const startNew = (from: string) => {
    const preset = networks.data?.networks.find((row) => row.key === from);
    setEditing({
      key: '',
      name: '',
      json: JSON.stringify(preset ? preset.contract : BLANK, null, 2),
      isNew: true,
    });
  };

  return (
    <div style={{ marginTop: 26 }}>
      <h3>{t('networks.title')}</h3>
      <p className="tiny muted">{t('networks.hint')}</p>

      {notice && (
        <div className={notice.kind === 'ok' ? 'notice ok' : 'notice error'}>{notice.text}</div>
      )}

      <ul className="plain-list">
        {(networks.data?.networks ?? []).map((row) => (
          <li key={row.key} className="card">
            <header className="card-head">
              <h4>
                {row.name} <span className="mono tiny muted ltr">{row.key}</span>
              </h4>
              <span className={`badge ${row.status === 'ACTIVE' ? 'ok' : 'warn'}`}>{row.status}</span>
            </header>
            <p className="tiny muted">
              {t(`networks.source.${row.source}` as TranslationKey)} ·{' '}
              {row.verifiedLive ? t('networks.verifiedLive') : t('networks.notVerified')}
            </p>
            <div className="actions">
              <button type="button" className="ghost small" onClick={() => open(row)}>
                {t('networks.edit')}
              </button>
              <button
                type="button"
                className="ghost small"
                disabled={status.isPending}
                onClick={() =>
                  status.mutate({ key: row.key, status: row.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE' })
                }
              >
                {row.status === 'ACTIVE' ? t('networks.disable') : t('networks.enable')}
              </button>
              {row.source !== 'PRESET' && (
                <button
                  type="button"
                  className="danger small"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(t('networks.confirmDelete'))) remove.mutate(row.key);
                  }}
                >
                  {row.source === 'OVERRIDE' ? t('networks.reset') : t('networks.delete')}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {!editing && (
        <div className="inline-form">
          <label htmlFor="network-from">{t('networks.fromPreset')}</label>
          <select
            id="network-from"
            defaultValue=""
            onChange={(event) => {
              startNew(event.target.value);
              event.target.value = '';
            }}
          >
            <option value="" disabled>
              {t('networks.add')}
            </option>
            <option value="__blank">{t('networks.blank')}</option>
            {(networks.data?.networks ?? []).map((row) => (
              <option key={row.key} value={row.key}>
                {row.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {editing && (
        <form
          className="card composer"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <label htmlFor="network-key">{t('networks.key')}</label>
          <input
            id="network-key"
            className="ltr"
            value={editing.key}
            disabled={!editing.isNew}
            onChange={(event) => setEditing({ ...editing, key: event.target.value })}
            maxLength={31}
          />
          <label htmlFor="network-name">{t('networks.name')}</label>
          <input
            id="network-name"
            value={editing.name}
            onChange={(event) => setEditing({ ...editing, name: event.target.value })}
            maxLength={60}
          />
          <label htmlFor="network-contract">{t('networks.contract')}</label>
          <textarea
            id="network-contract"
            className="ltr mono small"
            rows={18}
            spellCheck={false}
            value={editing.json}
            onChange={(event) => setEditing({ ...editing, json: event.target.value })}
          />
          <div className="actions">
            <button type="button" className="ghost small" onClick={() => check.mutate()} disabled={check.isPending}>
              {t('networks.validate')}
            </button>
            <button
              type="submit"
              className="primary small"
              disabled={save.isPending || !editing.key.trim() || !editing.name.trim()}
            >
              {t('networks.save')}
            </button>
            <button type="button" className="ghost small" onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
